import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";

export type SemanticDisplayMode = "name" | "keys" | "columns" | "profile";

export type SemanticObjectNodeField = {
  id: string;
  name: string;
  data_type: string;
  primary_key?: boolean;
  /**
   * ADR-0212 §4.4 — how this column is restricted in the subject area on screen: by its own setting
   * everywhere, by this area, or not at all.
   */
  restricted: "everywhere" | "here" | null;
  exposure_mode: string;
};

/** A relationship-specific handle with a computed position along its wall. */
export type NodeHandleSpec = {
  id: string;
  type: "source" | "target";
  wall: "top" | "right" | "bottom" | "left";
  /** Percentage offset along the wall (0–100). */
  offset: number;
};

const WALL_POSITION: Record<NodeHandleSpec["wall"], Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
};

export type SemanticObjectNodeType = Node<
  {
    label: string;
    objectId: string;
    displayMode: SemanticDisplayMode;
    fields: SemanticObjectNodeField[];
    /** Per-relationship handles with evenly-distributed offsets along each wall. */
    handles?: NodeHandleSpec[];
  },
  "semanticObject"
>;

const MAX_NON_PK_VISIBLE = 10;

function exposureSubscript(mode: string): string {
  switch (mode) {
    case "masked":
      return "\u2098"; // ₘ
    case "aggregate_only":
      return "\u2090"; // ₐ
    case "restricted":
      return "\u1D63"; // ᵣ
    default:
      return "";
  }
}

/**
 * ADR-0212 §4.4 (C3) — the restricted-column marker: a lock in the type cell, filled when the column is
 * restricted everywhere (its own setting), outlined when this subject area restricts it. The shape tells
 * the two apart without colour; the title is the word.
 */
function RestrictedMark({ restricted }: { restricted: "everywhere" | "here" }) {
  const everywhere = restricted === "everywhere";
  const title = everywhere ? "Restricted everywhere" : "Restricted in this area";
  return (
    <span className={everywhere ? "semanticObjectNodeRestricted" : "semanticObjectNodeRestrictedHere"} title={title} role="img" aria-label={title}>
      <svg viewBox="0 0 10 12" width="8" height="10" aria-hidden="true">
        <path d="M2.5 5.5V3.75a2.5 2.5 0 0 1 5 0V5.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <rect x="1" y="5.5" width="8" height="5.75" rx="1.2" fill={everywhere ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.1" />
      </svg>
    </span>
  );
}

function TypeCell({ field }: { field: SemanticObjectNodeField }) {
  return (
    <span className="semanticObjectNodeTypeCell mono">
      {field.restricted ? <RestrictedMark restricted={field.restricted} /> : null}
      {field.data_type}
    </span>
  );
}

type SemanticObjectNodeProps = NodeProps<SemanticObjectNodeType> & {
  onFieldRowClick?: (fieldId: string) => void;
};

/** Four wall handles, each usable as source or target (ConnectionMode.Loose). */
export function SemanticObjectNode(props: SemanticObjectNodeProps) {
  const { data, onFieldRowClick } = props;
  const { label, displayMode, fields, handles = [] } = data;
  const pks = fields.filter((f) => f.primary_key);
  const nonPk = fields.filter((f) => !f.primary_key);
  const showBody = displayMode !== "name";
  const showProfile = displayMode === "profile";

  const visibleNonPk = nonPk.slice(0, MAX_NON_PK_VISIBLE);
  const hiddenNonPkCount = nonPk.length - visibleNonPk.length;

  const rowClass = showProfile ? "semanticObjectNodeRow semanticObjectNodeRowProfile" : "semanticObjectNodeRow";

  return (
    <div className={`semanticObjectNode semanticObjectNodeDm${displayMode.charAt(0).toUpperCase() + displayMode.slice(1)}`}>
      {/* Static center handles — used for dragging new connections */}
      <Handle type="target" position={Position.Top} id="top-t" className="semanticObjectHandle" />
      <Handle type="target" position={Position.Right} id="right-t" className="semanticObjectHandle" />
      <Handle type="target" position={Position.Bottom} id="bottom-t" className="semanticObjectHandle" />
      <Handle type="target" position={Position.Left} id="left-t" className="semanticObjectHandle" />
      <Handle type="source" position={Position.Top} id="top" className="semanticObjectHandle" />
      <Handle type="source" position={Position.Right} id="right" className="semanticObjectHandle" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="semanticObjectHandle" />
      <Handle type="source" position={Position.Left} id="left" className="semanticObjectHandle" />
      {/* Dynamic per-relationship handles — invisible, just anchor edge endpoints */}
      {handles.map((h) => (
        <Handle
          key={h.id}
          id={h.id}
          type={h.type}
          position={WALL_POSITION[h.wall]}
          className="semanticObjectHandle"
          style={{
            [h.wall === "top" || h.wall === "bottom" ? "left" : "top"]: `${h.offset}%`,
            opacity: 0,
            pointerEvents: "none"
          }}
        />
      ))}
      <div className="semanticObjectNodeTitle">{label}</div>
      {showBody ? (
        <>
          <div className="semanticObjectNodeDivider" />
          <div className="semanticObjectNodeBody">
            {displayMode === "keys"
              ? pks.map((f) => (
                  <div
                    key={f.id}
                    className={rowClass}
                    role={onFieldRowClick ? "button" : undefined}
                    tabIndex={onFieldRowClick ? 0 : undefined}
                    onClick={
                      onFieldRowClick
                        ? (e) => {
                            e.stopPropagation();
                            onFieldRowClick(f.id);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onFieldRowClick
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              onFieldRowClick(f.id);
                            }
                          }
                        : undefined
                    }
                    style={onFieldRowClick ? { cursor: "pointer" } : undefined}
                  >
                    <span className="semanticObjectNodePkCell" aria-hidden>
                      🔑
                    </span>
                    <span className="semanticObjectNodeNameCell">{f.name}</span>
                    <TypeCell field={f} />
                    {showProfile ? (
                      <span className="semanticObjectNodeSecCell">
                        <span className="semanticObjectNodeExpSub">{exposureSubscript(f.exposure_mode)}</span>
                      </span>
                    ) : null}
                  </div>
                ))
              : null}

            {(displayMode === "columns" || displayMode === "profile") && (
              <>
                {pks.map((f) => (
                  <div
                    key={f.id}
                    className={rowClass}
                    role={onFieldRowClick ? "button" : undefined}
                    tabIndex={onFieldRowClick ? 0 : undefined}
                    onClick={
                      onFieldRowClick
                        ? (e) => {
                            e.stopPropagation();
                            onFieldRowClick(f.id);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onFieldRowClick
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              onFieldRowClick(f.id);
                            }
                          }
                        : undefined
                    }
                    style={onFieldRowClick ? { cursor: "pointer" } : undefined}
                  >
                    <span className="semanticObjectNodePkCell" aria-hidden>
                      🔑
                    </span>
                    <span className="semanticObjectNodeNameCell">{f.name}</span>
                    <TypeCell field={f} />
                    {showProfile ? (
                      <span className="semanticObjectNodeSecCell">
                        <span className="semanticObjectNodeExpSub">{exposureSubscript(f.exposure_mode)}</span>
                      </span>
                    ) : null}
                  </div>
                ))}
                {pks.length > 0 && nonPk.length > 0 ? <div className="semanticObjectNodeRowSep" aria-hidden /> : null}
                {visibleNonPk.map((f) => (
                  <div
                    key={f.id}
                    className={rowClass}
                    role={onFieldRowClick ? "button" : undefined}
                    tabIndex={onFieldRowClick ? 0 : undefined}
                    onClick={
                      onFieldRowClick
                        ? (e) => {
                            e.stopPropagation();
                            onFieldRowClick(f.id);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onFieldRowClick
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              onFieldRowClick(f.id);
                            }
                          }
                        : undefined
                    }
                    style={onFieldRowClick ? { cursor: "pointer" } : undefined}
                  >
                    <span className="semanticObjectNodePkCell muted" aria-hidden>
                      ──
                    </span>
                    <span className="semanticObjectNodeNameCell">{f.name}</span>
                    <TypeCell field={f} />
                    {showProfile ? (
                      <span className="semanticObjectNodeSecCell">
                        <span className="semanticObjectNodeExpSub">{exposureSubscript(f.exposure_mode)}</span>
                      </span>
                    ) : null}
                  </div>
                ))}
                {hiddenNonPkCount > 0 ? (
                  <div className={`${rowClass} semanticObjectNodeMoreRow`}>
                    <span className="semanticObjectNodePkCell" />
                    <span className="semanticObjectNodeNameCell muted">… +{hiddenNonPkCount} more</span>
                    {showProfile ? (
                      <>
                        <span className="semanticObjectNodeTypeCell" />
                        <span className="semanticObjectNodeSecCell" />
                      </>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
